import { ReactNode, useState } from 'react';
import { CSVReader } from 'react-papaparse';
import { CsvFieldAnalysis, GetHeaderRow, GetUnmappedFields, GetUnmappedFileFields } from './GetFieldData';
import { CsvMappingApiModel, CsvMappingItemApiModel } from '~/lib/api/smartsend/types';
import { FieldMappings } from './FieldMappings';
import { uid } from '../../lib/helpers/uid';
import AlertWithIcon from '../AlertWithIcon';
import { CsvRawDataRow } from './CsvRawDataRow';
import { CsvRawDataRowSanitiser, CsvRawDataRowSanitiserSummary } from './CsvRawDataRowSanitiser';
import { CsvMappingApplier } from './CsvMappingApplier';
import { CsvSmartSendMapResult, MapCsvDataToSmartSendItem } from './MapCsvDataToSmartSendItem';
import { CsvUploadSummary } from './CsvUploadSummary';
import { P2GBox, P2GDropdown, P2GProgress, P2GText, P2GButton, SvgConfirmCircle } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { SidePanelContent } from '~/components/sidepanel-section';
import { ConfigSetting, useTenant } from '@parcel2go/npm-p2g-app';
import { useMeasurementSystem } from '~/hooks/use-measurement-system';
interface props {
  onSave: (items: CsvSmartSendMapResult[], originalHeaders: string[]) => void;
  mappings: CsvMappingApiModel[];
  saveDefaultMappings: (selected: number, toSave: CsvMappingItemApiModel[]) => void;
  label?: ReactNode;
}

export const CsvUpload: React.FC<props> = (props) => {
  const [error, setError] = useState('');
  const { unit } = useMeasurementSystem();
  const { settings } = useTenant();
  const [summary, setSummary] = useState<CsvRawDataRowSanitiserSummary | null>(null);
  const [processedItems, setProcessedItems] = useState<CsvSmartSendMapResult[]>([]);
  const [rawFileData, setRawFileData] = useState<Array<CsvRawDataRow> | null>();
  const [unmappedFileHeaders, setUnmappedFileHeaders] = useState<CsvFieldAnalysis[]>([]);
  const [unmappedMappableFields, setUnmappedMappableFields] = useState<CsvFieldAnalysis[]>([]);
  const [stage, setStage] = useState<
    'INITIAL' | 'PROCESSING' | 'MAPPING' | 'CREATING' | 'CONFIRM' | 'FORMAT' | 'EMPTY' | 'ERROR'
  >('INITIAL');
  const [selectedMapping, setSelectedMapping] = useState<number | undefined>(props.mappings[0]?.Id);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  function handleOnDrop(data: Array<CsvRawDataRow>, file?: { name: string }) {
    if (file && file.name?.indexOf('.csv') == -1) {
      setStage('FORMAT');
      return;
    }

    // Remove Blanks
    const summary = CsvRawDataRowSanitiser(data);

    // Swap out some mapping
    summary.items = CsvMappingApplier(summary.items, props.mappings, selectedMapping);
    setSummary(summary.summary);

    setRawFileData(summary.items);
    const ufh = GetUnmappedFileFields(summary.items);
    setUnmappedFileHeaders(ufh);
    setUnmappedMappableFields(GetUnmappedFields(summary.items));
    if (ufh.length === 0) {
      process(summary.items);
    } else {
      setStage('MAPPING');
    }
  }

  function process(d: CsvRawDataRow[]) {
    setUnmappedFileHeaders([]);
    setUnmappedMappableFields([]);
    const res = MapCsvDataToSmartSendItem(d, settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY], unit);
    setProcessedItems(res);
    setStage(res.length === 0 ? 'EMPTY' : 'CONFIRM');
  }

  function handleOnError(_err: any, _file: any, _inputElem: any, reason: any) {
    reset();
    setError(reason);
    setStage('ERROR');
  }

  function handleOnRemoveFile(_data: null) {
    reset();
  }

  function reset() {
    setStage('INITIAL');
    setError('');
    setRawFileData(null);
    setUnmappedFileHeaders([]);
    setUnmappedMappableFields([]);
    setProcessedItems([]);
  }

  function doImport() {
    setStage('PROCESSING');
    props.onSave(processedItems, GetHeaderRow(rawFileData));
  }

  const showMapping = props.mappings?.length > 1;

  return (
    <P2GBox>
      {stage === 'INITIAL' && (
        <SidePanelContent>
          {showMapping && (
            <P2GBox mb={2}>
              <P2GDropdown
                options={props.mappings.map((x) => {
                  return { value: x.Id.toString(), text: x.Name };
                })}
                onChange={(v: string) => setSelectedMapping(parseInt(v))}
                value={selectedMapping?.toString()}
                label={t('SELECTED_MAPPING_INPUT_LABEL', { defaultValue: 'CSV Field Mapping' })}
                id="Selected Mapping"
              />
            </P2GBox>
          )}
          <CSVReader
            style={{ dropArea: { borderColor: '#999', backgroundColor: '#FFF' } }}
            onDrop={handleOnDrop}
            onError={handleOnError}
            addRemoveButton
            config={{
              skipEmptyLines: true
            }}
            removeButtonColor="#999"
            onRemoveFile={handleOnRemoveFile}
          >
            <P2GBox pt={2} pb={2}>
              {props.label || (
                <P2GText>{t('DROP_CSV_FILE', { defaultValue: 'Drop CSV file here or click to upload.' })}</P2GText>
              )}
            </P2GBox>
          </CSVReader>
        </SidePanelContent>
      )}

      {stage === 'PROCESSING' && (
        <SidePanelContent>
          <AlertWithIcon icon={<P2GProgress />} color="info">
            <P2GText>
              {t('PROCESSING_UPLOAD_ALERT', { defaultValue: "We're currently processing your upload..." })}
            </P2GText>
          </AlertWithIcon>
          <P2GBox>
            <P2GButton disabled>{t('PROCESSING_LOADING_BUTTON', { defaultValue: 'Loading...' })}</P2GButton>
          </P2GBox>
        </SidePanelContent>
      )}

      {stage === 'FORMAT' && (
        <SidePanelContent>
          <AlertWithIcon color="error">
            <P2GText>
              {t('UNABLE_TO_IMPORT_CSV_FILE_ERROR', {
                defaultValue: "We're unable to import this file. Please check the file is a .csv."
              })}
            </P2GText>
          </AlertWithIcon>
          <P2GBox>
            <P2GButton onClick={reset}>{t('FORMAT_RESET_BUTTON', { defaultValue: 'Reset' })}</P2GButton>
          </P2GBox>
        </SidePanelContent>
      )}

      {stage === 'ERROR' && (
        <SidePanelContent>
          <AlertWithIcon color="error">
            <P2GText>{error}</P2GText>
          </AlertWithIcon>
          <P2GBox>
            <P2GButton onClick={reset}>{t('ERROR_RESET_BUTTON', { defaultValue: 'Reset' })}</P2GButton>
          </P2GBox>
        </SidePanelContent>
      )}

      {stage === 'EMPTY' && (
        <SidePanelContent>
          <AlertWithIcon color="info">
            <P2GText>{t('NO_ITEMS_ALERT', { defaultValue: 'There are no items in your file.' })}</P2GText>
          </AlertWithIcon>
          <P2GBox>
            <P2GButton onClick={reset}>{t('NO_ITEMS_RESET_BUTTON', { defaultValue: 'Reset' })}</P2GButton>
          </P2GBox>
        </SidePanelContent>
      )}

      {stage === 'CONFIRM' && (
        <SidePanelContent>
          <AlertWithIcon iconSrc="/theme/icons/ConfirmCircle.svg" color="info" icon={<SvgConfirmCircle />}>
            <P2GText>
              <Trans
                t={t}
                i18nKey="CSV_ITEMS_READY_TO_UPLOAD"
                values={{ count: processedItems.length }}
                defaults={'There are {{count}} item(s) in your file that are ready to upload.'}
              />
            </P2GText>
          </AlertWithIcon>
          {summary?.errors > 0 && <CsvUploadSummary summary={summary} />}
          <P2GBox>
            <P2GButton variant="primary" onClick={doImport}>
              {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
            </P2GButton>
          </P2GBox>
        </SidePanelContent>
      )}

      {stage === 'MAPPING' && (
        <SidePanelContent>
          <AlertWithIcon color="info">
            <P2GText>
              <Trans
                t={t}
                i18nKey="CSV_ITEMS_UNMAPPED_FIELDS"
                values={{ count: unmappedFileHeaders.length }}
                defaults={
                  "There are {{count}} field(s) which we don't recognise. Choose below how you would like this information uploading."
                }
              />
            </P2GText>
          </AlertWithIcon>
          <FieldMappings
            unmapped={unmappedFileHeaders}
            mappable={unmappedMappableFields}
            update={(fields) => {
              const mappingsToSave: CsvMappingItemApiModel[] = [];
              const d = [...rawFileData];
              fields.forEach((f) => {
                if (f.saveMapping) {
                  mappingsToSave.push({
                    From: d[0].data[f.index],
                    To: f.field,
                    Id: uid()
                  });
                }
                d[0].data[f.index] = f.field;
              });
              setRawFileData(d);
              process(d);

              // Save Default Mappings
              if (mappingsToSave.length > 0) {
                props.saveDefaultMappings(selectedMapping, mappingsToSave);
              }
            }}
          />
        </SidePanelContent>
      )}
    </P2GBox>
  );
};
